/**
 * App — the route table. All session state lives in useAuth; the Console's screens are
 * declared in engine/consoleScreens.js and fetch their own data.
 *
 * There is ONE shell. The Console has no RBAC and no view modes: it is a
 * single-audience customer surface (ADR 0004 / E06) whose only user type is the
 * paying customer, and the operator/research dual view this file used to select
 * between has been removed along with its shell, tab registry and four tabs. RBAC
 * belongs to Argus, which is a separate product with its own identity store (E07).
 *
 * ROUTING (ADR 0009, superseding the navigation half of ADR 0001). This file was a
 * cascade of early returns over state, in a precedence order whose comments carried
 * most of the reasoning: verify-email ahead of the auth gate, the card step ahead of
 * the shell, and so on. Most of that precedence is now STRUCTURAL rather than
 * conventional, because the five emailed/returned landings are distinct URLs and
 * distinct URLs cannot contend. What remains genuinely ordered is maintenance, which
 * still preempts the whole table below and is therefore still an early return.
 *
 * The landing PATHS are unchanged and are not ours to change: the backend mints
 * /verify-email, /reset-password and /account-closure into emails, and Stripe mints
 * the two /registration returns. Renaming one here would break links already sent.
 */

import { useState } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router";
import LoginScreen from "./components/LoginScreen";
import RegisterScreen from "./components/RegisterScreen";
import AccountClosure from "./components/AccountClosure";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PaymentStep from "./components/PaymentStep";
import ConsoleShell from "./components/ConsoleShell";
import MaintenanceNotice from "./components/MaintenanceNotice";
import useAuth from "./hooks/useAuth";
import { SCREENS, DEFAULT_SCREEN } from "./engine/consoleScreens";
import { ROUTES } from "./engine/routePaths";

/**
 * Read a query parameter ONCE, at mount.
 *
 * Deliberately not a live `useSearchParams()` read. A13's closure token is scrubbed
 * from the address bar while the component is still on screen and still needs the
 * value for its one POST, and F14's completeReset drops `?token` without leaving
 * /reset-password at all. A live read would blank the token underneath both of them;
 * the previous routerless code had the same once-at-mount semantics via a useState
 * initializer, so this preserves rather than changes behaviour.
 */
function useInitialParam(name) {
  const [searchParams] = useSearchParams();
  const [value] = useState(() => searchParams.get(name) || "");
  return value;
}

/** L01 — /verify-email?token=<raw>. Public: the customer following it is not signed in. */
function VerifyEmailRoute({ onVerified, onBackToLogin }) {
  const token = useInitialParam("token");
  return <VerifyEmail token={token} onVerified={onVerified} onBackToLogin={onBackToLogin} />;
}

/**
 * F14 — /reset-password?token=<raw>. Public for the reason the verify link is: a
 * customer who cannot sign in is exactly who follows this link. Unlike verify it does
 * NOT auto-login; completeReset tears the session down instead (FR10).
 */
function ResetPasswordRoute({ onResetComplete, onBackToLogin, onRequestNewLink }) {
  const token = useInitialParam("token");
  return (
    <ResetPassword
      token={token}
      onResetComplete={onResetComplete}
      onBackToLogin={onBackToLogin}
      onRequestNewLink={onRequestNewLink}
    />
  );
}

/**
 * A13 — /account-closure?token=<raw>&action=confirm|cancel.
 *
 * `action` ONLY preselects a button; the page acts on a click and never on arrival,
 * because a mail scanner or link preloader fetches the URL unasked and here that
 * would close an account nobody confirmed. Anything other than the two known values
 * is dropped rather than passed through, so an unrecognised one leaves the customer
 * choosing rather than silently highlighting something.
 */
function AccountClosureRoute({ onLeave }) {
  const token = useInitialParam("token");
  const rawAction = useInitialParam("action");
  const action = rawAction === "confirm" || rawAction === "cancel" ? rawAction : "";
  return <AccountClosure token={token} action={action} onLeave={onLeave} />;
}

export default function App() {
  const {
    loggedIn, registrationSession, sessionKey, maintenance,
    authenticating, user, setUser, displayName, password, setPassword,
    loginLog, loginError, loginNotice, needsVerification,
    doLogin, logout,
    handleRegistered, handleVerified, leaveVerify, resendVerification, openRegister,
    handleRegistrationComplete, handleCardOnFile,
    resendingVerification, resendCooldownRemaining,
    completeReset, leaveReset, requestNewResetLink,
    leaveClosure,
    openForgot, closeForgot, backToLogin,
  } = useAuth();

  const location = useLocation();

  // OMEGA-1024 FR10 — maintenance, AHEAD OF EVERY ROUTE. An operator has stopped
  // new workload platform-wide, so neither the authenticated shell nor the card step nor
  // any auth screen renders: the API refuses a submit and a registration, and a screen
  // that still offers them is the broken-looking product US2 exists to prevent. The two
  // emailed landings are included deliberately, since both end in a registration
  // the gate would refuse anyway.
  //
  // It stays an early return ABOVE <Routes> rather than becoming a route of its own,
  // and that is the point: a route would have to out-rank eleven siblings by
  // convention, where this outranks them by construction. Nothing is torn down to get
  // here and nothing is restored on the way out. The session and its token are
  // untouched, so when the next poll comes back available this returns to whichever
  // URL was showing, with no reload and no re-login (FR12).
  //
  // The in-flight panel rides along only for a session that can actually read jobs: a
  // registration session holds a token every customer route answers 403 to, so asking it
  // for a job list would buy a refusal rather than a panel. A logged-out visitor sees
  // the plain notice (FR21) — the notice is never a reason to put a login form on screen
  // during a stop.
  if (maintenance) {
    return (
      <MaintenanceNotice
        message={maintenance.message}
        since={maintenance.since}
        showInFlight={loggedIn && !registrationSession}
      />
    );
  }

  // E23.6 — the only session kind every customer route admits. Anything else is a
  // registration session and belongs on the card step.
  const fullSession = loggedIn && !registrationSession;

  // Where "/" resolves to, and where an authenticated redirect lands.
  const home = !loggedIn
    ? ROUTES.login
    : registrationSession
      ? ROUTES.registrationCard
      : `/${DEFAULT_SCREEN}`;

  // A deep link followed while signed out is remembered so the customer lands where
  // they were going rather than on the default screen. `from` is carried in history
  // state, never in a query string: it is a UI affordance and has no business being
  // shareable or bookmarkable.
  //
  // `search` and `hash` are restored too, and that is not completeness for its own sake.
  // A session expiring during Stripe checkout returns the customer to
  // /billing/credit?topup=<id>: the pathname alone gets them to the right screen with the
  // payment outcome stripped off, which is the same failure as not routing the return at
  // all. Whatever was deep-linked to is restored whole.
  const from = location.state?.from;
  const afterLogin = from?.pathname
    ? `${from.pathname}${from.search || ""}${from.hash || ""}`
    : `/${DEFAULT_SCREEN}`;

  return (
    <Routes>
      {/* ── Public landings. Minted elsewhere (backend emails, Stripe returns), so
          reachable in ANY session state — including signed in, which is how the
          verify link both verifies and signs the customer in. ── */}
      <Route
        path={ROUTES.verifyEmail}
        element={<VerifyEmailRoute onVerified={handleVerified} onBackToLogin={leaveVerify} />}
      />
      <Route
        path={ROUTES.resetPassword}
        element={
          <ResetPasswordRoute
            onResetComplete={completeReset}
            onBackToLogin={leaveReset}
            onRequestNewLink={requestNewResetLink}
          />
        }
      />
      <Route path={ROUTES.accountClosure} element={<AccountClosureRoute onLeave={leaveClosure} />} />

      {/* ── The card step (E23.9 FR1), for a session whose stored token is a
          registration session. Both Stripe return URLs land here; useCardStep reads
          which landing this is from the route and scrubs it.

          Keyed on whose session it is: another account's registration token, stored by
          another tab, mounts a fresh step, so nothing the previous account's step still
          has in flight can land. A session that is not a registration session has no
          business here and is sent home — which for a full session is the Console and
          for a signed-out visitor is the login screen. ── */}
      {[ROUTES.registrationCard, ROUTES.registrationComplete].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            loggedIn && registrationSession ? (
              <PaymentStep
                key={sessionKey}
                onComplete={handleRegistrationComplete}
                onCardOnFile={handleCardOnFile}
                onLogout={logout}
              />
            ) : (
              <Navigate to={home} replace />
            )
          }
        />
      ))}

      {/* ── Auth screens. Each is its own URL, so the authView state machine that
          switched between them is gone. A signed-in visitor is sent on rather than
          shown a login form. ── */}
      <Route
        path={ROUTES.login}
        element={
          loggedIn ? (
            <Navigate to={afterLogin} replace />
          ) : (
            <LoginScreen
              user={user} setUser={setUser}
              password={password} setPassword={setPassword}
              loginLog={loginLog} onConnect={doLogin}
              onRegister={openRegister}
              error={loginError}
              authenticating={authenticating}
              needsVerification={needsVerification}
              onResendVerification={resendVerification}
              resendingVerification={resendingVerification}
              resendCooldown={resendCooldownRemaining}
              onForgotPassword={openForgot}
              notice={loginNotice}
            />
          )
        }
      />
      <Route
        path={ROUTES.register}
        element={
          loggedIn ? (
            <Navigate to={home} replace />
          ) : (
            <RegisterScreen onRegistered={handleRegistered} onBackToLogin={backToLogin} />
          )
        }
      />
      <Route
        path={ROUTES.forgotPassword}
        element={loggedIn ? <Navigate to={home} replace /> : <ForgotPassword onBackToLogin={closeForgot} />}
      />

      {/* ── The authenticated shell. A pathless layout route: ConsoleShell renders the
          chrome and an <Outlet/>, and each screen below is a child URL. When the guard
          fails the layout IS the redirect, so no child mounts and no screen-level
          check is needed — the gate is held in one place rather than six.

          `from` is attached only on the signed-out redirect, since that is the only
          one a login can complete. A registration session is sent to the card step
          with no `from`: it has a screen of its own to finish first. ── */}
      <Route
        element={
          fullSession ? (
            <ConsoleShell user={user} displayName={displayName} onLogout={logout} />
          ) : loggedIn ? (
            <Navigate to={ROUTES.registrationCard} replace />
          ) : (
            <Navigate to={ROUTES.login} state={{ from: location }} replace />
          )
        }
      >
        {SCREENS.map((s) => {
          const Screen = s.component;
          // A screen's path is its id unless the registry overrides it, which two do:
          // Jobs for its shareable `/jobs/:jobId` detail, and Billing for E21's Stripe
          // top-up return at `/billing/credit`. Both use an optional trailing segment,
          // so each screen keeps ONE route and the same element stays mounted across
          // the swap, which is what lets JobsView's poll keep running while a detail is
          // open. The reasons live on the entries in engine/consoleScreens.js.
          const path = s.routePath ?? s.id;
          return <Route key={s.id} path={path} element={<Screen />} />;
        })}
      </Route>

      {/* ── Root and catch-all. Both resolve by session state rather than guessing, so
          a stale bookmark or a typo lands on a real screen. `replace`, so neither
          leaves a dead entry the back button would bounce off. ── */}
      <Route path={ROUTES.root} element={<Navigate to={home} replace />} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}
